import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AppShell,
  Badge,
  Card,
  parseServicesConfig,
  findActiveServiceId,
  Icons,
  IconByName,
  type ServiceEntry,
  type SidebarItem,
} from '@butternetwork/shared-ui';
import {
  parseExternalLinks,
  groupExternalLinks,
  logoUrlFor,
  type ExternalLink,
} from '../lib/externalLinks';

const PORTAL_SIDEBAR: SidebarItem[] = [
  { label: '全部服务', to: '/', icon: <Icons.GridIcon className="w-4 h-4" /> },
  { label: '最近访问', to: '/recent', icon: <Icons.MonitorIcon className="w-4 h-4" /> },
  { label: '审计日志', to: '/audit', icon: <Icons.CopyIcon className="w-4 h-4" /> },
  { label: '系统设置', to: '/settings', icon: <Icons.MonitorIcon className="w-4 h-4" /> },
];

export function Portal() {
  const location = useLocation();
  const services = parseServicesConfig(
    (import.meta as any).env?.VITE_SERVICES_CONFIG as string | undefined,
  );
  const externalLinks = parseExternalLinks(
    (import.meta as any).env?.VITE_EXTERNAL_LINKS_CONFIG as string | undefined,
  );
  const externalGroups = groupExternalLinks(externalLinks);
  const activeServiceId = findActiveServiceId(services, location.pathname);
  const homeUrl = (import.meta as any).env?.VITE_SHELL_HOME_URL || '/';

  return (
    <AppShell
      appName="Portal"
      services={services}
      activeServiceId={activeServiceId}
      sidebarItems={PORTAL_SIDEBAR}
      activeSidebarPath={location.pathname}
      homeUrl={homeUrl}
    >
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <header>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
            Operations Hub
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            选择一个子系统进入。可用服务由 <code className="text-xs px-1 py-0.5 rounded bg-muted">VITE_SERVICES_CONFIG</code> 控制。
          </p>
        </header>

        {/* Quick stats strip */}
        <section className="grid grid-cols-3 gap-3 sm:gap-4">
          <Stat label="Services" value={String(services.length)} />
          <Stat label="Status" value="All up" tone="ok" />
          <Stat label="Last incident" value="14d ago" />
        </section>

        {services.length === 0 ? (
          <EmptyState />
        ) : (
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              All services
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((svc) => (
                <ServiceCard key={svc.id} service={svc} />
              ))}
            </ul>
          </section>
        )}

        {externalGroups.length > 0 && (
          <section className="space-y-4 pt-2">
            <header>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                External services
              </h2>
              <p className="mt-1 text-[11px] text-muted-foreground/70">
                项目依赖的外部服务的快捷入口。由{' '}
                <code className="text-[11px] px-1 py-0.5 rounded bg-muted">
                  VITE_EXTERNAL_LINKS_CONFIG
                </code>{' '}
                控制；每个链接都在新 tab 打开。
              </p>
            </header>

            {externalGroups.map((group) => (
              <div key={group.name}>
                <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80 mb-2">
                  {group.name}
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {group.items.map((link) => (
                    <ExternalLinkRow key={link.id} link={link} />
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}
      </div>
    </AppShell>
  );
}

function ExternalLinkRow({ link }: { link: ExternalLink }) {
  return (
    <li>
      <a
        href={link.url}
        target="_blank"
        rel="noreferrer"
        title={link.url}
        className="group flex items-center gap-2.5 px-3 py-2 rounded-lg border bg-card hover:border-primary/40 hover:bg-muted/40 transition-colors"
      >
        <ExternalLinkLogo link={link} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium truncate">{link.name}</span>
          {link.desc && (
            <span className="block text-[11px] text-muted-foreground truncate">
              {link.desc}
            </span>
          )}
        </span>
        <Icons.ExternalLinkIcon className="w-3 h-3 text-muted-foreground/40 group-hover:text-primary shrink-0" />
      </a>
    </li>
  );
}

function ExternalLinkLogo({ link }: { link: ExternalLink }) {
  // Try the real logo / favicon first; fall back to the lucide icon on load
  // failure (private network, CDN blocked, no favicon, etc.).
  const [logoFailed, setLogoFailed] = useState(false);
  const logoSrc = logoFailed ? null : logoUrlFor(link);

  // White container behind logos so single-color brand icons (Vercel's black
  // triangle, GitHub's Octocat, etc.) stay visible in both light and dark
  // mode. Lucide fallback keeps the standard muted-on-muted styling since
  // those use currentColor and follow theme.
  if (logoSrc) {
    return (
      <span className="w-7 h-7 rounded-md bg-white dark:bg-white/95 ring-1 ring-border/30 flex items-center justify-center shrink-0 overflow-hidden">
        <img
          src={logoSrc}
          alt=""
          loading="lazy"
          className="w-4 h-4 object-contain"
          onError={() => setLogoFailed(true)}
        />
      </span>
    );
  }

  return (
    <span className="w-7 h-7 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
      <IconByName
        name={link.iconName}
        className="w-3.5 h-3.5"
        fallback={<Icons.ExternalLinkIcon className="w-3.5 h-3.5" />}
      />
    </span>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
}) {
  const valueClass =
    tone === 'ok'
      ? 'text-emerald-700 dark:text-emerald-400'
      : tone === 'warn'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-foreground';

  return (
    <Card className="px-4 py-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={'mt-1 text-base sm:text-lg font-semibold ' + valueClass}>
        {value}
      </div>
    </Card>
  );
}

function ServiceCard({ service }: { service: ServiceEntry }) {
  const isExternal = /^https?:\/\//.test(service.url);
  const description = service.desc ?? '';
  const iconNode = service.icon ?? (
    <IconByName
      name={service.iconName}
      className="w-4 h-4"
      fallback={<Icons.GridIcon className="w-4 h-4" />}
    />
  );

  if (service.comingSoon) {
    return (
      <li>
        <Card
          aria-disabled="true"
          title="建设中"
          className="h-full p-5 border-dashed bg-muted/40 cursor-not-allowed select-none"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-muted text-muted-foreground/60">
              {iconNode}
            </span>
            <Badge variant="warning" className="text-[10px] uppercase tracking-wide">
              建设中
            </Badge>
          </div>

          <h3 className="text-base font-semibold text-muted-foreground">{service.name}</h3>

          {description && (
            <p className="mt-1 text-xs text-muted-foreground/70 leading-relaxed">{description}</p>
          )}

          <p className="mt-3 text-[11px] font-mono text-muted-foreground/40 truncate">{service.url}</p>
        </Card>
      </li>
    );
  }

  return (
    <li>
      <a
        href={service.url}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer' : undefined}
        className="block h-full"
      >
        <Card className="group h-full p-5 hover:border-primary/60 hover:bg-primary/5 hover:-translate-y-0.5 transition-all">
          <div className="flex items-start justify-between gap-3 mb-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-sky-400/15 to-indigo-500/15 dark:from-sky-500/20 dark:to-indigo-600/20 text-primary group-hover:from-sky-400/25 group-hover:to-indigo-500/25 transition-colors">
              {iconNode}
            </span>
            <Icons.ChevronRightIcon className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
          </div>

          <h3 className="text-base font-semibold">
            {service.name}
            {isExternal && (
              <Icons.ExternalLinkIcon className="inline-block w-3 h-3 ml-1.5 -translate-y-px text-muted-foreground" />
            )}
          </h3>

          {description && (
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{description}</p>
          )}

          <p className="mt-3 text-[11px] font-mono text-muted-foreground/60 truncate">{service.url}</p>
        </Card>
      </a>
    </li>
  );
}

function EmptyState() {
  return (
    <Card className="border-dashed p-8 text-center bg-muted/30">
      <h2 className="text-sm font-semibold">No services configured</h2>
      <p className="mt-2 text-xs text-muted-foreground">
        Set <code className="px-1 py-0.5 rounded bg-muted">VITE_SERVICES_CONFIG</code>{' '}
        in <code>apps/shell/.env</code> and restart dev.
      </p>
    </Card>
  );
}
